import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ApiError, authedJson } from '@/lib/http';
import { queryClient } from '@/lib/queryClient';

export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      return (JSON.parse(err.body) as { error?: string }).error ?? err.message;
    } catch {
      return err.body || err.message;
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

const PASS_QUERIES = [['/api/classes/user/passes'], ['/api/passes/transfers/sent']];

function refreshPassQueries() {
  for (const queryKey of PASS_QUERIES) void queryClient.invalidateQueries({ queryKey });
  // Class pages cache their own balance under ['/api/classes', id, 'my-pass'].
  void queryClient.invalidateQueries({
    predicate: q => q.queryKey[0] === '/api/classes' && q.queryKey[2] === 'my-pass',
  });
}

/** "Send punches" — transfer or gift punches from one of the student's passes. */
export function SendPunchesDialog({
  passId,
  passName,
  remaining,
  open,
  onOpenChange,
}: {
  passId: string;
  passName: string;
  remaining: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [punches, setPunches] = useState('1');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState<{ claimUrl: string; emailed: boolean } | null>(null);

  const reset = () => {
    setPunches('1');
    setRecipientEmail('');
    setRecipientName('');
    setMessage('');
    setSent(null);
  };

  const sendMutation = useMutation({
    mutationFn: () =>
      authedJson<{ claimUrl: string; emailed: boolean }>(`/api/passes/${passId}/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          punches: parseInt(punches, 10),
          recipientEmail: recipientEmail.trim(),
          recipientName: recipientName.trim() || null,
          message: message.trim() || null,
        }),
      }),
    onSuccess: data => {
      setSent(data);
      refreshPassQueries();
    },
    onError: err =>
      toast({ title: 'Could not send', description: apiErrorMessage(err), variant: 'destructive' }),
  });

  const count = parseInt(punches, 10);
  const valid =
    Number.isInteger(count) &&
    count >= 1 &&
    count <= remaining &&
    /\S+@\S+\.\S+/.test(recipientEmail);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{sent ? 'Punches sent' : 'Send punches'}</DialogTitle>
          <DialogDescription>
            {sent
              ? sent.emailed
                ? 'We emailed them a link to claim the punches. You can also share this link directly:'
                : "We couldn't send the email — share this link with them directly:"
              : `Transfer or gift punches from your ${passName}. They leave your pass now and go back to you if you cancel before they're claimed.`}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={sent.claimUrl} onFocus={e => e.currentTarget.select()} />
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard?.writeText(sent.claimUrl).then(
                    () => toast({ title: 'Link copied' }),
                    () => undefined
                  );
                }}
              >
                Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              This link is shown only once. Anyone with it can claim the punches.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="send-punches">Punches (you have {remaining})</Label>
              <Input
                id="send-punches"
                type="number"
                min={1}
                max={remaining}
                value={punches}
                onChange={e => setPunches(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-email">Their email</Label>
              <Input
                id="send-email"
                type="email"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-name">Their name (optional)</Label>
              <Input
                id="send-name"
                value={recipientName}
                onChange={e => setRecipientName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-message">Note (optional)</Label>
              <Textarea
                id="send-message"
                rows={2}
                maxLength={500}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {sent ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => sendMutation.mutate()}
                disabled={!valid || sendMutation.isPending}
                data-testid="button-send-punches"
              >
                {sendMutation.isPending
                  ? 'Sending…'
                  : `Send ${Number.isInteger(count) && count > 0 ? count : ''} ${count === 1 ? 'punch' : 'punches'}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SentTransfer {
  id: string;
  punches: number;
  recipientEmail: string;
  recipientName: string | null;
  status: 'pending' | 'claimed' | 'canceled';
  createdAt: string;
}

/** Punches the student has sent, with Cancel on ones not yet claimed. */
export function SentTransfersList() {
  const { toast } = useToast();
  const { data } = useQuery<{ transfers: SentTransfer[] }>({
    queryKey: ['/api/passes/transfers/sent'],
    queryFn: () => authedJson('/api/passes/transfers/sent'),
  });

  const cancelMutation = useMutation({
    mutationFn: (transferId: string) =>
      authedJson(`/api/passes/transfers/${transferId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast({ title: 'Transfer canceled', description: 'The punches are back on your pass.' });
      refreshPassQueries();
    },
    onError: err =>
      toast({
        title: 'Could not cancel',
        description: apiErrorMessage(err),
        variant: 'destructive',
      }),
  });

  const transfers = data?.transfers ?? [];
  if (transfers.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Punches you&apos;ve sent</h3>
      {transfers.map(t => (
        <div
          key={t.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm"
        >
          <div className="min-w-0">
            <p>
              {t.punches} {t.punches === 1 ? 'punch' : 'punches'} to{' '}
              {t.recipientName ? `${t.recipientName} (${t.recipientEmail})` : t.recipientEmail}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(t.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={t.status === 'pending' ? 'secondary' : 'outline'}>
              {t.status === 'pending'
                ? 'Waiting to be claimed'
                : t.status === 'claimed'
                  ? 'Claimed'
                  : 'Canceled'}
            </Badge>
            {t.status === 'pending' && (
              <Button
                size="sm"
                variant="outline"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate(t.id)}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
